import { authHeaders, client } from '../../_shared/services/openapiClient';
import type { paths } from '../../../types/schema';

export type LikeAdaptedFictionEditionParams =
  paths['/api/v1/adapted_fiction_editions/{id}/like']['post']['parameters'];
export type LikeAdaptedFictionEditionResponse =
  paths['/api/v1/adapted_fiction_editions/{id}/like']['post']['responses']['200']['content']['application/json']['adaptedFictionEdition'];

export type GetAdaptedFictionEditionCommentsParams =
  paths['/api/v1/adapted_fiction_editions/{adapted_fiction_edition_id}/comments']['get']['parameters']['query'];
export type GetAdaptedFictionEditionCommentsResponse =
  paths['/api/v1/adapted_fiction_editions/{adapted_fiction_edition_id}/comments']['get']['responses']['200']['content']['application/json'];

export type CreateAdaptedFictionEditionCommentBody = NonNullable<
  paths['/api/v1/adapted_fiction_editions/{adapted_fiction_edition_id}/comments']['post']['requestBody']
>['content']['application/json'];
export type CreateAdaptedFictionEditionCommentResponse =
  paths['/api/v1/adapted_fiction_editions/{adapted_fiction_edition_id}/comments']['post']['responses']['201']['content']['application/json']['comment'];

export type LikeAdaptedFictionEditionBody = NonNullable<
  paths['/api/v1/adapted_fiction_editions/{id}/like']['post']['requestBody']
>['content']['application/json'];
export type LikeAdaptedFictionEditionCommentResponse =
  paths['/api/v1/adapted_fiction_editions/{adapted_fiction_edition_id}/comments/{id}/like']['post']['responses']['200']['content']['application/json']['comment'];

export type GetAdaptedFictionEditionRepliesParams =
  paths['/api/v1/adapted_fiction_editions/{adapted_fiction_edition_id}/comments/{id}/replies']['get']['parameters']['query'];
export type GetAdaptedFictionEditionRepliesResponse =
  paths['/api/v1/adapted_fiction_editions/{adapted_fiction_edition_id}/comments/{id}/replies']['get']['responses']['200']['content']['application/json'];

export type GetCommentDetailResponse =
  paths['/api/v1/adapted_fiction_editions/{adapted_fiction_edition_id}/comments/{id}']['get']['responses']['200']['content']['application/json']['comment'];

export type DeleteAdaptedFictionEditionCommentResponse =
  paths['/api/v1/adapted_fiction_editions/{adapted_fiction_edition_id}/comments/{id}']['delete']['responses']['200']['content']['application/json']['comment'];


export async function likeAdaptedFictionEdition({
  adaptedFictionEditionId,
  body
}: {
  adaptedFictionEditionId: string;
  body: LikeAdaptedFictionEditionBody;
}): Promise<LikeAdaptedFictionEditionResponse> {
  const { data, error } = await client.POST('/api/v1/adapted_fiction_editions/{id}/like', {
    params: {
      path: {
        id: adaptedFictionEditionId
      }
    },
    body,
    headers: authHeaders()
  });
  if (error) throw error;
  if (!data) throw new Error('Empty response when liking adapted fiction edition');
  return data.adaptedFictionEdition;
}

export async function getAdaptedFictionEditionComments({
  adaptedFictionEditionId,
  params
}: {
  adaptedFictionEditionId: string;
  params: GetAdaptedFictionEditionCommentsParams;
}): Promise<GetAdaptedFictionEditionCommentsResponse> {
  const { data, error } = await client.GET(
    '/api/v1/adapted_fiction_editions/{adapted_fiction_edition_id}/comments',
    {
      params: {
        path: {
          adapted_fiction_edition_id: adaptedFictionEditionId
        },
        query: params
      },
      headers: authHeaders()
    }
  );
  if (error) throw error;
  if (!data) throw new Error('Empty response when getting adapted fiction edition comments');
  return data;
}

export async function createAdaptedFictionEditionComment({
  adaptedFictionEditionId,
  body
}: {
  adaptedFictionEditionId: string;
  body: CreateAdaptedFictionEditionCommentBody;
}): Promise<CreateAdaptedFictionEditionCommentResponse> {
  const { data, error } = await client.POST(
    '/api/v1/adapted_fiction_editions/{adapted_fiction_edition_id}/comments',
    {
      params: {
        path: {
          adapted_fiction_edition_id: adaptedFictionEditionId
        }
      },
      body,
      headers: authHeaders()
    }
  );
  if (error) throw error;
  if (!data) throw new Error('Empty response when creating adapted fiction edition comment');
  return data.comment;
}

export async function likeAdaptedFictionEditionComment({
  adaptedFictionEditionId,
  commentId
}: {
  adaptedFictionEditionId: string;
  commentId: string;
}): Promise<LikeAdaptedFictionEditionCommentResponse> {
  const { data, error } = await client.POST(
    '/api/v1/adapted_fiction_editions/{adapted_fiction_edition_id}/comments/{id}/like',
    {
      params: {
        path: {
          adapted_fiction_edition_id: adaptedFictionEditionId,
          id: commentId
        }
      },
      headers: authHeaders()
    }
  );
  if (error) throw error;
  if (!data) throw new Error('Empty response when liking adapted fiction edition comment');
  return data.comment;
}

export async function getAdaptedFictionEditionReplies({
  adaptedFictionEditionId,
  commentId,
  params
}: {
  adaptedFictionEditionId: string;
  commentId: string;
  params: GetAdaptedFictionEditionRepliesParams;
}): Promise<GetAdaptedFictionEditionRepliesResponse> {
  const { data, error } = await client.GET(
    '/api/v1/adapted_fiction_editions/{adapted_fiction_edition_id}/comments/{id}/replies',
    {
      params: {
        path: {
          adapted_fiction_edition_id: adaptedFictionEditionId,
          id: commentId
        },
        query: params
      },
      headers: authHeaders()
    }
  );
  if (error) throw error;
  if (!data) throw new Error('Empty response when getting adapted fiction edition replies');
  return data;
}

export async function getCommentDetail({
  adaptedFictionEditionId,
  commentId
}: {
  adaptedFictionEditionId: string;
  commentId: string;
}): Promise<GetCommentDetailResponse> {
  const { data, error } = await client.GET(
    '/api/v1/adapted_fiction_editions/{adapted_fiction_edition_id}/comments/{id}',
    {
      params: {
        path: {
          adapted_fiction_edition_id: adaptedFictionEditionId,
          id: commentId
        }
      },
      headers: authHeaders()
    }
  );
  if (error) throw error;
  if (!data) throw new Error('Empty response when getting comment detail');
  return data.comment;
}

export async function deleteAdaptedFictionEditionComment({
  adaptedFictionEditionId,
  commentId
}: {
  adaptedFictionEditionId: string;
  commentId: string;
}): Promise<DeleteAdaptedFictionEditionCommentResponse> {
  const { data, error } = await client.DELETE(
    '/api/v1/adapted_fiction_editions/{adapted_fiction_edition_id}/comments/{id}',
    {
      params: {
        path: {
          adapted_fiction_edition_id: adaptedFictionEditionId,
          id: commentId
        }
      },
      headers: authHeaders()
    }
  );
  if (error) throw error;
  if (!data) throw new Error('Empty response when deleting adapted fiction edition comment');
  return data.comment;
}